import { useCallback, useState, useEffect } from 'react';
import { router, usePage } from '@inertiajs/react';
import classNames from 'classnames';
import styled from 'styled-components';
import Clarity from '@microsoft/clarity';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import { parseISO, differenceInDays, startOfISOWeek, addDays, getISODay, getUnixTime } from 'date-fns';
import { dateFormat, parseDate, useTranslations } from '@/app/i18n.js';
import Modal from '@/app/Components/Modal.jsx';
import GTMEventManager from '@/app/GTMEventManager.js';
import CloseIcon from '@assets/icons/close.svg?react';
import LogoMarker from '@assets/icons/marker-pin.svg';

const MapContainer = styled('div')`
    .gmnoprint,
    .gm-style-cc[draggable="false"] {
        display: none;
        visibility: hidden;
    }
    z-index: 1;
    padding: 00px;
    position: absolute;
    width: 100%;
    height: 42rem;
    top: 0;
    left: 0;
    &:after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 100%;
        box-shadow: #1a1a1a 0px 0px 100px 80px inset;
        background: rgb(26,26,26);
        background: linear-gradient(16deg, rgba(26,26,26,1) 0%, rgba(26,26,26,0.8) 42%, rgba(26,26,26,0.1) 60%, rgba(26,26,26,0) 100%);
    },
    &:before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 100%;
        // background: rgb(26,26,26);
        z-index: 1;
        background: linear-gradient(90deg, rgba(26,26,26,1) 0%, rgba(26,26,26,0.75) 40%, rgba(26,26,26,0.3) 50%, rgba(26,26,26,0) 100%);
    }
`;

export default function ({ selected, pointsOfSale, onSelectPointOfSale }) {
    const page = usePage();
    const { uniq_id: uniqId, session_id: sessionId } = page.props.auth;
    const __ = useTranslations();
    const [currentPointOfSale, setCurrentPointOfSale] = useState(pointsOfSale[0]);
    const [showOpeningHours, setShowOpeningHours] = useState(false);

    useEffect(() => {
        GTMEventManager.pageView('step_points_of_sale');
        console.log(`[Clarity] session: ${uniqId} / ${sessionId} - step_points_of_sale`);
        Clarity.identify(uniqId, sessionId, 'step_points_of_sale');
    }, []);

    useEffect(() => {
        if (!selected) {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const { latitude, longitude } = position.coords;
                        const query = new URLSearchParams(window.location.search);
                        query.set('latitude', latitude);
                        query.set('longitude', longitude);
                        router.visit(route('booking.game', { point_of_sale: selected?.slug ?? null }) + '?' + query.toString(), { preserveState: true });
                    },
                    (error) => {
                        console.error('Error getting user location:', error);
                    }
                );
            }
        }
    }, [selected]);

    const handleShowOpeningHours = useCallback((pointOfSale) => {
        pointOfSale.exceptions = {};
        pointOfSale.holidays = [];

        // Will parse single date exceptions
        for (let i = 0; i < 7; i++) {
            const day = addDays(startOfISOWeek(new Date()), i);
            const dayDate = dateFormat(day, 'yyyy-MM-dd');

            const dayExceptions = Object.keys(pointOfSale.opening_hours.exceptions).filter((d) => d === dayDate);
            if (dayExceptions.length > 0) {
                pointOfSale.exceptions[dateFormat(day, 'EEEE').toLowerCase()] = pointOfSale.opening_hours.exceptions[dayDate];
            }
        }

        // Will parse range dates
        Object.keys(pointOfSale.opening_hours.exceptions)
            .filter((e) => e.indexOf(' to ') !== -1)
            .forEach((exception) => {
                // is days range
                const dates = exception.split(' to ');
                const yearRecurrent = dates[0].split('-').length === 2;

                const currentYear = new Date().getFullYear();
                const startParts = dates[0].split('-');
                const endParts = dates[1].split('-');

                const start = new Date(
                    yearRecurrent ? currentYear : parseInt(startParts[0]),
                    (yearRecurrent ? parseInt(startParts[0]) : parseInt(startParts[1])) - 1,
                    yearRecurrent ? parseInt(startParts[1]) : parseInt(startParts[2])
                );

                const end = new Date(
                    yearRecurrent ? currentYear : parseInt(endParts[0]),
                    (yearRecurrent ? parseInt(endParts[0]) : parseInt(endParts[1])) - 1,
                    yearRecurrent ? parseInt(endParts[1]) : parseInt(endParts[2])
                );

                if (differenceInDays(start, new Date()) < 360) {
                    pointOfSale.holidays.push({ exception, start, end });
                }
            });

        pointOfSale.holidays.sort((a, b) => getUnixTime(a.start) - getUnixTime(b.start));

        setCurrentPointOfSale(pointOfSale);
        setShowOpeningHours(true);
        GTMEventManager.pageView('opening_hours', {
            point_of_sale_id: pointOfSale.id,
        });
    }, []);

    const openAtLabel = (pointOfSale) => {
        let label = '';
        const now = new Date();
        const openAt = parseISO(pointOfSale.openRange.end);
        const daysBetween = differenceInDays(openAt, now);

        if (daysBetween <= 1) {
            if (getISODay(now) !== getISODay(openAt)) {
                label += __('book.index.steps.location.points_of_sale.tomorrow_label');
            }
        } else {
            label += '<span class="">' + dateFormat(openAt, 'EEEE do MMMM') + '</span>';
        }

        label += ' ' + __('book.index.steps.location.points_of_sale.at_label') + ' ';
        label += dateFormat(openAt, 'HH\'h\'mm');

        return label;
    };

    const formatRange = (range) => {
        return range.split('-').map((r) => {
            const today = dateFormat(new Date(), 'yyyy-MM-dd');
            const dateTime = parseDate(`${today} ${r}`, 'yyyy-MM-dd HH:mm', new Date());
            return dateFormat(dateTime, 'p');
        }).join(' / ');
    };

    const exceptionLabel = (exception, weekDay) => {
        if (exception.hasOwnProperty('hours') && exception.hours.length > 0) {
            const today = dateFormat(new Date(), 'yyyy-MM-dd');
            const hours = exception.hours[0].split('-').map((r) => {
                const dateTime = parseDate(`${today} ${r}`, 'yyyy-MM-dd HH:mm', new Date());
                return dateFormat(dateTime, 'p');
            });

            return __('book.index.steps.location.opening_hours.exception_hours_label', {
                start: hours[0],
                end: hours[1]
            });
        }

        const date = addDays(startOfISOWeek(new Date()), weekDay);
        return __('book.index.steps.location.opening_hours.exception_date_label', {
            date: dateFormat(date, 'EEEE do MMMM Y')
        });
    };

    return (
        <section className="flex flex-col gap-10 px-3 lg:px-5 pb-4 sm:pb-8 pt-0 dark:pt-4 lg:dark:pt-0">
            <div
                className="px-0 relative grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 3xl:grid-cols-3 gap-4 3xl:gap-6">
                {pointsOfSale.map((pointOfSale) => (
                    <div
                        key={pointOfSale.slug}
                        className="flex p-2 relative border border-gray-925 rounded-md warm-shadow"
                    >
                        <img
                            src={pointOfSale.main_poster}
                            alt={pointOfSale.name}
                            className="w-full rounded cursor-pointer"
                            onClick={() => onSelectPointOfSale(pointOfSale)}
                        />
                        <div
                            className="absolute flex flex-row items-center justify-between bottom-2 3lx:bottom-4 text-gray-100 px-3 3xl:px-4 py-3 sm:py-4 3xl:py-6"
                            style={{background: 'rgba(15, 15, 15, 0.9)', width: 'calc(100% - 0.75rem'}}
                        >
                            <div className="flex flex-col gap-3 w-full">
                                <div className="flex flex-row items-center justify-between cursor-pointer">
                                    <h2
                                        className="text-md 3xl:text-2xl font-space font-bold"
                                        onClick={() => onSelectPointOfSale(pointOfSale)}
                                    >
                                        {pointOfSale.name}
                                    </h2>
                                    <button
                                        className="bg-white text-black dark:bg-light-gray dark:opacity-75 hover:opacity-90 transition rounded-2xl py-1 pl-3 pr-1 flex flex-row dark:text-black items-center gap-2 cursor-pointer"
                                        onClick={() => handleShowOpeningHours(pointOfSale)}
                                    >
                                        <span className="text-xs 3xl:text-sm font-semibold">{__('book.index.steps.location.points_of_sale.opening_hours_cta')}</span>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="size-5 3xl:size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                        </svg>
                                    </button>
                                </div>
                                <div className="hidden lg:flex flex-row items-center justify-between text-sm 3xl:text-base">
                                    <a href={pointOfSale.google_map_url} target="_blank" className="whitespace-nowrap text-ellipsis truncate">
                                        {pointOfSale.address}
                                    </a>
                                    {pointOfSale.geoMatrix && (
                                        <div className="flex flex-row items-center gap-2">
                                            <span className="font-semibold">{__('book.index.steps.location.points_of_sale.distance_label', {distance: pointOfSale.geoMatrix.distance.text})}</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="absolute top-2 right-2 rounded-bl-lg text-gray-200 pt-1.5 pb-1.5 px-4" style={{background: 'rgba(15, 15, 15, 0.6)'}}>
                            <div className="flex flex-row items-center gap-3">
                                <span className="relative flex size-2">
                                    <span className={classNames({
                                        'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75': true,
                                        'bg-red-500': !pointOfSale.open,
                                        'bg-green-500': pointOfSale.open,
                                    })} />
                                    <span className={classNames({
                                        'relative inline-flex rounded-full size-2': true,
                                        'bg-red-500': !pointOfSale.open,
                                        'bg-green-500': pointOfSale.open,
                                    })} />
                                </span>
                                <div className="text-sm 3xl:text-base text-white">
                                    <span>
                                        {pointOfSale.open ? __('book.index.steps.location.points_of_sale.open_label') : __('book.index.steps.location.points_of_sale.closed_label')}
                                    </span>
                                    {pointOfSale.open && (
                                        <span> —&nbsp; {__('book.index.steps.location.points_of_sale.close_at_label', {
                                            time: dateFormat(parseISO(pointOfSale.openRange.end), 'HH\'h\'mm')
                                        })}</span>
                                    )}
                                    {!pointOfSale.open && (
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: ' —&nbsp; ' + __('book.index.steps.location.points_of_sale.open_at_label', {
                                                    date: openAtLabel(pointOfSale)
                                                })
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <Modal show={showOpeningHours} onClose={() => setShowOpeningHours(false)} maxWidth="5xl">
                {currentPointOfSale && (
                    <div className="flex flex-col py-6 sm:py-7 px-8 sm:px-8">
                        {currentPointOfSale.location?.latitude != null && currentPointOfSale.location?.longitude != null && <MapContainer>
                            <APIProvider apiKey={page.props.google_map_api_key}>
                                <Map
                                    style={{width: '100%', height: '100%'}}
                                    center={{
                                        lat: currentPointOfSale.location.latitude - 0.02,
                                        lng: currentPointOfSale.location.longitude - 0.07
                                    }}
                                    styles={[
                                        {
                                            "elementType": "geometry",
                                            "stylers": [
                                                {
                                                    "color": "#1a1a1a"
                                                }
                                            ]
                                        },
                                        {
                                            "elementType": "labels.icon",
                                            "stylers": [
                                                {
                                                    "visibility": "off"
                                                }
                                            ]
                                        },
                                        {
                                            "elementType": "labels.text.fill",
                                            "stylers": [
                                                {
                                                    "color": "#757575"
                                                }
                                            ]
                                        },
                                        {
                                            "elementType": "labels.text.stroke",
                                            "stylers": [
                                                {
                                                    "color": "#212121"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "administrative",
                                            "elementType": "geometry",
                                            "stylers": [
                                                {
                                                    "color": "#757575"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "administrative.country",
                                            "elementType": "labels.text.fill",
                                            "stylers": [
                                                {
                                                    "color": "#9e9e9e"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "administrative.land_parcel",
                                            "stylers": [
                                                {
                                                    "visibility": "off"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "administrative.locality",
                                            "elementType": "labels.text.fill",
                                            "stylers": [
                                                {
                                                    "color": "#bdbdbd"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "poi",
                                            "elementType": "labels.text.fill",
                                            "stylers": [
                                                {
                                                    "color": "#757575"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "poi.park",
                                            "elementType": "geometry",
                                            "stylers": [
                                                {
                                                    "color": "#181818"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "poi.park",
                                            "elementType": "labels.text.fill",
                                            "stylers": [
                                                {
                                                    "color": "#616161"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "poi.park",
                                            "elementType": "labels.text.stroke",
                                            "stylers": [
                                                {
                                                    "color": "#1b1b1b"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "road",
                                            "elementType": "geometry.fill",
                                            "stylers": [
                                                {
                                                    "color": "#2c2c2c"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "road",
                                            "elementType": "labels.text.fill",
                                            "stylers": [
                                                {
                                                    "color": "#8a8a8a"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "road.arterial",
                                            "elementType": "geometry",
                                            "stylers": [
                                                {
                                                    "color": "#373737"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "road.highway",
                                            "elementType": "geometry",
                                            "stylers": [
                                                {
                                                    "color": "#3c3c3c"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "road.highway.controlled_access",
                                            "elementType": "geometry",
                                            "stylers": [
                                                {
                                                    "color": "#4e4e4e"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "road.local",
                                            "elementType": "labels.text.fill",
                                            "stylers": [
                                                {
                                                    "color": "#616161"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "transit",
                                            "elementType": "labels.text.fill",
                                            "stylers": [
                                                {
                                                    "color": "#757575"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "water",
                                            "elementType": "geometry",
                                            "stylers": [
                                                {
                                                    "color": "#000000"
                                                }
                                            ]
                                        },
                                        {
                                            "featureType": "water",
                                            "elementType": "labels.text.fill",
                                            "stylers": [
                                                {
                                                    "color": "#3d3d3d"
                                                }
                                            ]
                                        }
                                    ]}
                                    defaultZoom={12}
                                    disableDefaultUI
                                    gestureHandling={'greedy'}
                                >
                                    <Marker
                                        position={{lat: currentPointOfSale.location.latitude, lng: currentPointOfSale.location.longitude}}
                                        icon={{
                                            url: LogoMarker,
                                            size: { height: 64, width: 64 },
                                            scaledSize: { height: 64, width: 64 },
                                        }}
                                    />
                                </Map>
                            </APIProvider>
                        </MapContainer>}
                        <div className="flex flew-row items-center justify-between text-gray-200 relative z-10">
                            <h3 className="text-3xl font-semibold text-gray-100">{__('book.index.steps.location.opening_hours.title', {pointOfSale: currentPointOfSale.name})}</h3>
                            <div
                                className="rounded-full p-2.5 sm:p-4 cursor-pointer bg-smooth-gray transition hover:bg-dark-gray text-light-gray hover:text-white"
                                onClick={() => setShowOpeningHours(false)}
                            >
                                <CloseIcon className="size-5"/>
                            </div>
                        </div>
                        <div className="flex flex-col mt-6 text-gray-200 relative z-10">
                            <h2 className="text-2xl 3xl:text-3xl font-semibold mb-2 text-gray-300">{currentPointOfSale.name}</h2>
                            <a
                                href={currentPointOfSale.google_map_url}
                                target="_blank"
                                className="text-base 3xl:text-lg text-primary font-semibold transition hover:underline flex flew-row items-center gap-2"
                                tabIndex={-1}
                            >
                                <span>{currentPointOfSale.address}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                </svg>
                            </a>
                            <table className="mb-4 mt-6 3xl:mt-10">
                            <tbody>
                                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((dayOfWeek, i) => (
                                        <tr key={dayOfWeek}>
                                            <td width="142px" className="text-base 3xl:text-lg text-gray-100">
                                                <div
                                                    className={classNames({
                                                        'border-l my-1 py-2 px-4 capitalize': true,
                                                        'bg-gray-925 border-gray-900 ': getISODay(new Date()) !== i + 1,
                                                        'bg-gray-900 border-l-2 border-primary text-white opacity-90': getISODay(new Date()) === i + 1,
                                                    })}
                                                >
                                                    {__(`book.common.days_of_week.${dayOfWeek}`)}
                                                </div>
                                            </td>
                                            <td className="pl-4 text-base 3xl:text-lg text-gray-200">
                                                <div className="flex flex-row gap-4 my-1 leading-none">
                                                    {currentPointOfSale.opening_hours[dayOfWeek].length === 0 && (
                                                        <span>{__('book.index.steps.location.points_of_sale.closed_label')}</span>
                                                    )}
                                                    {currentPointOfSale.opening_hours[dayOfWeek].map((openingHour, i) => (
                                                        <div className="leading-none" key={`${dayOfWeek}-range-${i+Math.random()+100}`}>
                                                            <span className="leading-none">
                                                                {formatRange(openingHour)}
                                                                {currentPointOfSale.opening_hours[dayOfWeek].length > 1 && i < currentPointOfSale.opening_hours[dayOfWeek].length - 1 && (
                                                                    '   —'
                                                                )}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                                {currentPointOfSale.exceptions && currentPointOfSale.exceptions.hasOwnProperty(dayOfWeek) && (
                                                    <span className="block mt-0.5 text-red-400 text-sm">{exceptionLabel(currentPointOfSale.exceptions[dayOfWeek], i)}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {currentPointOfSale.holidays && currentPointOfSale.holidays.length > 0 && (
                                <div
                                    className="flex flex-row items-center gap-4 border-l-4 border-gray-900 bg-gray-925 text-gray-300 py-3 px-6 w-full mt-4 mb-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                                    </svg>
                                    <span>
                                        {
                                            __('book.index.steps.location.opening_hours.holidays_label', {
                                                start: dateFormat(currentPointOfSale.holidays[0].start, 'EEEE do MMMM Y'),
                                                end: dateFormat(currentPointOfSale.holidays[0].end, 'EEEE do MMMM Y'),
                                            })
                                        }
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </section>
    );
}
